import moment from "moment-timezone";

export const APP_TIMEZONE = "America/Chicago";

/**
 * Location business days (Central Time). Keep in sync with be/src/utils/locationHours.js.
 *
 * McAllen and Brownsville: 12:00 PM → 11:00 AM next day (blocked 11:00 AM – 12:00 PM)
 */
const SHARED_BUSINESS_HOURS = {
  startHour: 12,
  startMinute: 0,
  endHour: 11,
  endMinute: 0,
  endInclusive: false,
};

export const LOCATION_HOURS = {
  mcallen: {
    id: "mcallen",
    label: "McAllen",
    ...SHARED_BUSINESS_HOURS,
  },
  brownsville: {
    id: "brownsville",
    label: "Brownsville",
    ...SHARED_BUSINESS_HOURS,
  },
};

const pad2 = (value) => String(value).padStart(2, "0");

export const normalizeLocationId = (value) => {
  const id = String(value || "").trim().toLowerCase();
  return id === "mcallen" ? "mcallen" : "brownsville";
};

export const getLocationIdFromHost = (hostname = "") => {
  const host = String(hostname || "").toLowerCase();
  if (host.includes("brownsville") || host === "localhost" || host === "127.0.0.1") {
    return "brownsville";
  }
  if (host.includes("elitepokerlounge") || host.includes("mcallen")) {
    return "mcallen";
  }
  return "brownsville";
};

export const getLocationId = () => {
  const raw = String(process.env.REACT_APP_LOCATION_ID || "").trim().toLowerCase();
  if (raw === "mcallen" || raw === "brownsville") return raw;
  if (typeof window !== "undefined" && window.location?.hostname) {
    return getLocationIdFromHost(window.location.hostname);
  }
  return "brownsville";
};

export const getLocationHours = (locationId = getLocationId()) =>
  LOCATION_HOURS[normalizeLocationId(locationId)] || LOCATION_HOURS.brownsville;

export const formatClock = ({ hour, minute }) => {
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${pad2(minute)} ${period}`;
};

export const formatBusinessHoursLabel = (locationId = getLocationId()) => {
  const loc = getLocationHours(locationId);
  return `${formatClock({ hour: loc.startHour, minute: loc.startMinute })} – ${formatClock({
    hour: loc.endHour,
    minute: loc.endMinute,
  })}`;
};

export const formatAllLocationsHoursLabel = () =>
  `McAllen & Brownsville: ${formatBusinessHoursLabel("brownsville")} (CT)`;

export const calendarMoment = (dateLike) => {
  if (dateLike == null || dateLike === "") {
    return moment.tz(APP_TIMEZONE);
  }
  if (moment.isMoment(dateLike)) {
    return dateLike.clone().tz(APP_TIMEZONE);
  }
  if (typeof dateLike === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateLike.trim())) {
    return moment.tz(dateLike.trim(), "YYYY-MM-DD", APP_TIMEZONE);
  }
  return moment.tz(dateLike, APP_TIMEZONE);
};

export const businessDayStart = (dateLike, locationId = getLocationId()) => {
  const loc = getLocationHours(locationId);
  return calendarMoment(dateLike)
    .startOf("day")
    .hour(loc.startHour)
    .minute(loc.startMinute)
    .second(0)
    .millisecond(0);
};

export const businessDayEnd = (dateLike, locationId = getLocationId()) => {
  const loc = getLocationHours(locationId);
  const end = calendarMoment(dateLike)
    .startOf("day")
    .add(1, "day")
    .hour(loc.endHour)
    .minute(loc.endMinute)
    .second(0)
    .millisecond(0);

  if (loc.endInclusive) {
    return end.clone().add(1, "minute").subtract(1, "millisecond");
  }
  return end.clone().subtract(1, "millisecond");
};

export const currentBusinessDateKey = (nowLike, locationId = getLocationId()) => {
  const loc = getLocationHours(locationId);
  const current = calendarMoment(nowLike);
  const startToday = current
    .clone()
    .startOf("day")
    .hour(loc.startHour)
    .minute(loc.startMinute)
    .second(0)
    .millisecond(0);

  if (current.isSameOrAfter(startToday)) {
    return current.format("YYYY-MM-DD");
  }

  const endToday = current
    .clone()
    .startOf("day")
    .hour(loc.endHour)
    .minute(loc.endMinute)
    .second(0)
    .millisecond(0);
  const stillPrevious = loc.endInclusive
    ? current.isSameOrBefore(endToday)
    : current.isBefore(endToday);

  if (stillPrevious) {
    return current.clone().subtract(1, "day").format("YYYY-MM-DD");
  }

  return current.format("YYYY-MM-DD");
};

export const getBusinessDateRange = (startDateLike, endDateLike, locationId = getLocationId()) => ({
  start: businessDayStart(startDateLike, locationId).toDate(),
  end: businessDayEnd(endDateLike || startDateLike, locationId).toDate(),
});

export const getCurrentOperationalWindow = (nowLike, locationId = getLocationId()) => {
  const current = calendarMoment(nowLike);
  const businessKey = currentBusinessDateKey(current, locationId);
  return {
    now: current,
    startTime: businessDayStart(businessKey, locationId).toDate(),
    endTime: businessDayEnd(businessKey, locationId).toDate(),
  };
};

export const getVerificationWindowRange = (nowLike, locationId = getLocationId()) => {
  const yesterday = calendarMoment(nowLike).startOf("day").subtract(1, "day");
  return {
    start: businessDayStart(yesterday, locationId).toDate(),
    end: businessDayEnd(yesterday, locationId).toDate(),
  };
};

export const isWithinLocationOperationalWindow = (nowLike, locationId = getLocationId()) => {
  const { now, startTime, endTime } = getCurrentOperationalWindow(nowLike, locationId);
  return now.isBetween(moment(startTime), moment(endTime), null, "[]");
};

const previousDateKey = (year, month, day) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() - 1);
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
};

/** Floor clock time to a 30-minute slot label, matching dashboard charts. */
export const formatSlotLabel = (hour, minute) => {
  const isSecondHalf = minute >= 30;
  if (hour === 9 && minute >= 30) return "9:30 AM";
  if (hour === 6 && minute <= 30) return isSecondHalf ? "6:30 AM" : "6:00 AM";
  if (hour === 12) return isSecondHalf ? "12:30 PM" : "12:00 PM";
  if (hour === 0) return isSecondHalf ? "12:30 AM" : "12:00 AM";
  if (hour > 12) return isSecondHalf ? `${hour - 12}:30 PM` : `${hour - 12}:00 PM`;
  return isSecondHalf ? `${hour}:30 AM` : `${hour}:00 AM`;
};

/**
 * Assign an analytics timestamp to the location's operational calendar date.
 * Returns YYYY-MM-DD, or null when the clock falls in that location's closed gap.
 */
export const getOperationalDayKeyFast = (timestamp, locationId = "mcallen") => {
  if (!timestamp || typeof timestamp !== "string") return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(timestamp.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  let hour = Number(match[4]);
  const minute = Number(match[5]);
  const meridiem = match[6].toUpperCase();

  if (meridiem === "PM" && hour !== 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;

  const loc = getLocationHours(locationId);
  const totalMinutes = hour * 60 + minute;
  const startMinutes = loc.startHour * 60 + loc.startMinute;
  const endMinutes = loc.endHour * 60 + loc.endMinute;

  if (totalMinutes >= startMinutes) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const inMorning = loc.endInclusive ? totalMinutes <= endMinutes : totalMinutes < endMinutes;
  if (inMorning) {
    return previousDateKey(year, month, day);
  }

  return null;
};

export const getOperationalSlotIdFromTimestamp = (timestamp, businessDayKey) => {
  if (!timestamp || typeof timestamp !== "string" || !businessDayKey) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(timestamp.trim());
  if (!match) return null;

  const calKey = `${match[1]}-${match[2]}-${match[3]}`;
  let hour = Number(match[4]);
  const minute = Number(match[5]);
  const meridiem = match[6].toUpperCase();
  if (meridiem === "PM" && hour !== 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;

  const label = formatSlotLabel(hour, minute);
  const prefix = calKey <= businessDayKey ? "start" : "next";
  return `${prefix}|${label}`;
};

/** Shared 12:00 PM open through 11:00 AM next day close. */
export const buildCombinedOperationalSlots = () => {
  const slots = [];
  const dayMinutes = 24 * 60;
  const startAbs = 12 * 60;
  const endAbs = dayMinutes + 11 * 60;

  for (let abs = startAbs; abs <= endAbs; abs += 30) {
    const prefix = abs >= dayMinutes ? "next" : "start";
    const clock = ((abs % dayMinutes) + dayMinutes) % dayMinutes;
    const hour = Math.floor(clock / 60);
    const minute = clock % 60;
    const label = formatSlotLabel(hour, minute);
    slots.push({ id: `${prefix}|${label}`, label });
  }

  return slots;
};
